import type { TFunction } from 'i18next';

import {
  type BlockingRefusal,
  type PlanLimitRefusal,
  type QuotaRefusal,
  type SourceTablesRefusal,
  isQuotaRefusal,
  isTrialRefusal,
} from '@/model/client/planLimitRefusal';

export function sourceTablesRefusalMessage(
  refusal: Pick<SourceTablesRefusal, 'limit' | 'requested'>,
  t: TFunction,
): string {
  if (refusal.requested <= refusal.limit) {
    return t('dataset.tablesLimit.full', { limit: refusal.limit });
  }

  return t('dataset.tablesLimit.over', {
    limit: refusal.limit,
    requested: refusal.requested,
    count: refusal.requested - refusal.limit,
  });
}

export function resetDate(resetsAt: string | null, t: TFunction): string {
  const date = resetsAt ? new Date(resetsAt) : null;
  if (!date || Number.isNaN(date.getTime())) return t('dataset.quotaLimit.nextMonth');

  return t('dataset.quotaLimit.date', { date });
}

export function quotaRefusalMessage(refusal: QuotaRefusal, t: TFunction): string {
  const values = { limit: refusal.limit, used: refusal.used };
  if (refusal.key === 'storage_gb') return t('dataset.quotaLimit.storage', values);
  const date = resetDate(refusal.resetsAt, t);
  if (refusal.level === 'over_ceiling') {
    return t('dataset.quotaLimit.processingQueries', { ...values, date });
  }

  return t('dataset.quotaLimit.processing', { ...values, date });
}

export function trialExpiredMessage(t: TFunction): string {
  return t('trial.expired.refusal');
}

function blockingMessage(refusal: BlockingRefusal, t: TFunction): string {
  return isTrialRefusal(refusal) ? trialExpiredMessage(t) : quotaRefusalMessage(refusal, t);
}

export function limitedMessage(
  limited: { count: number; quota: BlockingRefusal | undefined },
  tablesKey: string,
  t: TFunction,
): string {
  return limited.quota ? blockingMessage(limited.quota, t) : t(tablesKey, { count: limited.count });
}

export function planLimitRefusalMessage(refusal: PlanLimitRefusal, t: TFunction): string {
  if (isTrialRefusal(refusal)) return trialExpiredMessage(t);

  return isQuotaRefusal(refusal)
    ? quotaRefusalMessage(refusal, t)
    : sourceTablesRefusalMessage(refusal, t);
}
